import {
  getBlob,
  getMetadata,
  StorageError,
  type StorageReference,
  ref as storageRef,
  deleteObject,
} from "firebase/storage";
import {
  getCachedImage,
  setCachedImage,
  generateKey,
  clearCachedImages,
} from "@/helpers/cache/image-cache";
import { ImageSizes } from "@/enums/images-sizes";
import { CacheTypes } from "@/enums/cache-types";
import { firebaseStorage } from "@/helpers/firebase/firebase";
import type {
  PictureFetchConfig,
  PictureResult,
  PictureDeleteConfig,
} from "@/types/picture";

/**
 * Fetch a picture from Firebase Storage with caching support
 *
 * This function implements the common pattern:
 * 1. Try to get from cache (with blurhash OR TTL validation)
 * 2. If cache miss, fetch from Firebase Storage using formatReference
 * 3. If not found, fallback to fallbackReference
 * 4. Cache the result
 *
 * @param config - Configuration for fetching the picture
 * @returns Picture result with blob, URL, and cache status
 * @throws Error if the picture cannot be fetched
 */
export async function fetchPicture(
  config: PictureFetchConfig,
): Promise<PictureResult> {
  const {
    cacheType,
    cacheId,
    formatReference,
    fallbackReference,
    format,
    callerTtlMs,
    setterTtlMs,
  } = config;

  const cacheKey = generateKey(cacheType, cacheId, format);

  // Try cache first (with MD5 validation)
  const cached = await getCachedImage(cacheKey, formatReference, callerTtlMs);

  if (cached) {
    console.log(`Cache hit for ${cacheType} ${cacheId} ${format}`);
    return {
      blob: cached.blob,
      format,
      url: cached.url,
      fromCache: true,
    };
  }

  // Cache miss - fetch from Firebase
  console.log(
    `Cache miss for ${cacheType} ${cacheId} ${format} - fetching from Firebase`,
  );

  try {
    const blob = await getBlob(formatReference);
    const url = URL.createObjectURL(blob);

    // Cache the image with MD5 hash (fail silently)
    await cacheImage(cacheKey, blob, formatReference, setterTtlMs);

    return {
      blob,
      format,
      url,
      fromCache: false,
    };
  } catch (e) {
    const error = e as StorageError;
    if (error.code === "storage/object-not-found") {
      // Could be that the image has not been resized yet - try fallback
      return await fetchFromFallback(
        cacheKey,
        fallbackReference,
        format,
        setterTtlMs,
      );
    } else {
      throw error;
    }
  }
}

/**
 * Fetch the fallback/original image
 */
async function fetchFromFallback(
  cacheKey: string,
  fallbackReference: StorageReference,
  format: ImageSizes,
  setterTtlMs?: number,
): Promise<PictureResult> {
  const blob = await getBlob(fallbackReference);
  const url = URL.createObjectURL(blob);

  // Cache the image with MD5 hash (fail silently)
  await cacheImage(cacheKey, blob, fallbackReference, setterTtlMs);

  return {
    blob,
    format,
    url,
    fromCache: false,
  };
}

/**
 * Cache an image with error handling
 */
async function cacheImage(
  cacheKey: string,
  blob: Blob,
  storageReference: StorageReference,
  setterTtlMs?: number,
): Promise<void> {
  try {
    // Fetch metadata to get MD5 hash
    let md5Hash: string | null = null;
    try {
      const metadata = await getMetadata(storageReference);
      md5Hash = metadata.md5Hash ?? null;
    } catch (metadataError) {
      console.warn(`Failed to fetch metadata for ${cacheKey}:`, metadataError);
      // Continue caching without MD5 hash
    }

    await setCachedImage(cacheKey, blob, setterTtlMs ?? null, md5Hash);
  } catch (cacheError) {
    console.error(`Failed to cache image with key ${cacheKey}:`, cacheError);
  }
}

/**
 * Delete a picture from Firebase Storage and clear its cache
 *
 * This function:
 * 1. Deletes all specified format variants from Firebase Storage
 * 2. Clears all cached versions of the image
 *
 * @param config - Configuration for deleting the picture
 * @returns Promise that resolves when deletion is complete
 */
export async function deletePicture(
  config: PictureDeleteConfig,
): Promise<void> {
  const {
    cacheType,
    cacheId,
    basePath,
    formats = [
      ImageSizes.ORIGINAL,
      ImageSizes.Thumb,
      ImageSizes.SD,
      ImageSizes.HD,
      ImageSizes.Full_hd,
      ImageSizes.Ultra_hd,
    ],
  } = config;

  // Delete all format variants from Storage
  const deletePromises = formats.map(async (format) => {
    try {
      const pictureRef = storageRef(firebaseStorage, basePath + format);
      await deleteObject(pictureRef);
      console.log(`Deleted ${cacheType} ${cacheId} ${format} from Storage`);
    } catch (error) {
      const storageError = error as StorageError;
      // Silently ignore if file doesn't exist - it may not have been generated yet
      if (storageError.code !== "storage/object-not-found") {
        console.error(
          `Failed to delete ${cacheType} ${cacheId} ${format}:`,
          error,
        );
      }
    }
  });

  await Promise.all(deletePromises);

  // Clear cached images for this picture
  try {
    await clearCachedImages(`${cacheType}:${cacheId}`);
    console.log(`Cleared cache for ${cacheType} ${cacheId}`);
  } catch (error) {
    console.error(`Failed to clear cache for ${cacheType} ${cacheId}:`, error);
  }
}
